import {
  createFrontendModule,
  type FrontendModule,
} from '@backstage/frontend-plugin-api';
import type { CatalogExportSettingsColumn } from '@backstage/plugin-catalog';
import { CatalogExportConfigBlueprint } from '@backstage/plugin-catalog/alpha';
import { yamlExporter } from './yamlExporter';

/**
 * The columns every export offers, in the order the dialog lists them.
 *
 * Providing this replaces the built-in list rather than extending it, so the
 * four stock columns are repeated here — dropping one of them is a deliberate
 * edit, not a side effect of adding the TeamSystem ones.
 *
 * `spec.area` and `spec.subarea` are the `Module` fields behind the Area and
 * Functional Domain columns of the table. Name leads because it is the one
 * column that means something for every kind; the two organisational
 * coordinates follow it, ahead of the stock rest, because that is roughly how
 * the Module table reads. For kinds that have no area — `User`, `Group`,
 * `Area` itself — they simply come out empty, and the dialog lets the user
 * untick them per export.
 *
 * One difference from the table worth knowing: an export column is a dot path
 * into the entity, so what lands in the file is the raw `spec.area` value — the
 * area entity's name, `hr-suite` — where the table renders an `EntityRefLink`
 * showing that area's display title. Resolving the title would mean fetching
 * the `Area` entities, which the `entityFilterKey` mechanism cannot do, and the
 * built-in CSV and JSON exporters have the same behaviour.
 */
const exportColumns: CatalogExportSettingsColumn[] = [
  { entityFilterKey: 'metadata.name', title: 'Name' },
  { entityFilterKey: 'spec.area', title: 'Area' },
  { entityFilterKey: 'spec.subarea', title: 'Functional Domain' },
  { entityFilterKey: 'spec.type', title: 'Type' },
  { entityFilterKey: 'spec.owner', title: 'Owner' },
  { entityFilterKey: 'metadata.description', title: 'Description' },
];

/**
 * The portal's catalog export configuration: a YAML format next to the built-in
 * CSV and JSON, and the column list above.
 *
 * This is the shape the catalog customization guide prescribes for export
 * customization: a `CatalogExportConfigBlueprint` extension in a frontend
 * module owned by the `catalog` plugin. The blueprint attaches itself to the
 * `exportConfig` input of `page:catalog`, so nothing has to be wired up beyond
 * listing this module in `createApp`.
 *
 * `onSuccess` and `onError` are also available on the blueprint and are
 * deliberately left unset: the page's own success and error toasts are the
 * right feedback.
 *
 * The extension id is `catalog-export-config:catalog`, and the format key
 * `yaml` is what names the downloaded file — `catalog-export.yaml`.
 */
const catalogExportConfig = CatalogExportConfigBlueprint.make({
  params: {
    exporters: {
      yaml: { exporter: yamlExporter, label: 'YAML' },
    },
    columns: exportColumns,
  },
});

export const catalogExportModule: FrontendModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [catalogExportConfig],
});
